import { useMemo } from "react";

import { CloudflowEngineModel, type VariableValue } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";

import { useApiContext } from "../../../api/context";
import { cloudflowTexts } from "../../../assets/texts";
import { useApiMutation } from "../../../Components/hooks/useApiMutation";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { getCachingKeys } from "../../../utils/cachingKeys";
import { type CreateGlobalVariableInput } from "./types";

type VariableScope = { type: "global" } | { type: "local"; flowId: string };

const useVariableManager = (scope: VariableScope) => {
  const { customer } = useCustomerContext();
  const api = useApiContext();
  const customerId = useCustomerId();

  const type = scope.type;
  const flowId = scope.type === "local" ? scope.flowId : undefined;

  const query = useMemo(() => {
    const baseQuery = getCollection(CloudflowEngineModel).doc("cloudflows");
    if (type === "global") {
      if (!customer?.id) {
        return undefined;
      }
      return baseQuery.collection("globalVariables").doc(customer.id).collection("variables");
    }
    if (!flowId) {
      return undefined;
    }
    return baseQuery.collection("cloudflowEntities").doc(flowId).collection("variables");
  }, [type, flowId, customer?.id]);

  const cachingKeys = useMemo(() => {
    const keys = getCachingKeys(customer?.id);
    if (type === "local" && flowId) {
      return [...keys, flowId];
    }
    return keys;
  }, [customer?.id, type, flowId]);

  const [variables, loading, error] = useCollectionData<VariableValue, "id", undefined>(query, {
    idField: "id",
    caching: true,
    cachingKeys,
  });

  const getEndpoint = (id?: string) => {
    const base =
      type === "global"
        ? `/v1/customers/${customerId}/cloudflow/global-variables`
        : `/v1/customers/${customerId}/cloudflow/${flowId}/variables`;
    return id ? `${base}/${id}` : base;
  };

  const getQueryKey = () => (type === "global" ? ["globalVariables", customerId] : ["flowVariables", flowId]);

  const typeName = type === "global" ? "GLOBAL" : "LOCAL";
  const queryKey = getQueryKey();

  const createVariableMutation = useApiMutation({
    mutationFn: (data: CreateGlobalVariableInput) => api.post(getEndpoint(), data),
    queryKeyToInvalidate: queryKey,
    successMessage: cloudflowTexts[`${typeName}_VARIABLE_CREATED_SUCCESSFULLY`],
    errorMessage: cloudflowTexts[`FAILED_TO_CREATE_${typeName}_VARIABLE`],
  });

  const editVariableMutation = useApiMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<CreateGlobalVariableInput, "owner"> }) =>
      api.put(getEndpoint(id), data),
    queryKeyToInvalidate: queryKey,
    successMessage: cloudflowTexts[`${typeName}_VARIABLE_EDITED_SUCCESSFULLY`],
    errorMessage: cloudflowTexts[`FAILED_TO_EDIT_${typeName}_VARIABLE`],
  });

  const deleteVariableMutation = useApiMutation({
    mutationFn: (id: string) => api.delete(getEndpoint(id)),
    queryKeyToInvalidate: queryKey,
    errorMessage: "An error occurred when deleting the variable",
  });

  return {
    variables,
    loading,
    error,
    createVariable: createVariableMutation.mutateAsync,
    editVariable: editVariableMutation.mutateAsync,
    deleteVariable: deleteVariableMutation.mutateAsync,
    isCreating: createVariableMutation.isPending,
    isEditing: editVariableMutation.isPending,
    isDeleting: deleteVariableMutation.isPending,
  };
};

export const useGlobalVariables = () => {
  const { variables, ...rest } = useVariableManager({ type: "global" });
  return {
    globalVariables: variables,
    ...rest,
  };
};

export const useLocalVariables = (flowId: string) => {
  const { variables, ...rest } = useVariableManager({ type: "local", flowId });
  return {
    localVariables: variables,
    ...rest,
  };
};
